import { useState } from 'react'
import { pushState } from './push-state'
import type { DeviceFacts } from './push-state'

/**
 * O que a tela oferece sobre push, conforme o que este aparelho consegue.
 *
 * ## A instalação é passo de onboarding real, não um banner discreto
 *
 * `Docs/07-Integracoes/Web Push.md` é explícito nas três: a instalação *"é passo
 * de onboarding real, com tela própria explicando o porquê"*; o app *"detecta se
 * está em modo standalone e, se não estiver, explica o que falta antes de sequer
 * pedir permissão"*; e a permissão é pedida *"em contexto — depois que a pessoa
 * entende o que vai receber — nunca no primeiro segundo"*.
 *
 * É por isso que esta seção fica **embaixo** da lista de preferências, e não no
 * topo: quando a pessoa chega aqui, ela já viu o que existe para receber.
 * *"Permissão negada é praticamente irreversível, e pedir cedo é o caminho mais
 * curto para a negativa."*
 *
 * ## Cada estado tem uma frase própria, e nenhuma delas é "algo deu errado"
 *
 * `needsInstall` e `unsupported` parecem o mesmo problema para quem escreve o
 * código e são opostos para quem usa: um tem conserto em quinze segundos, o
 * outro não tem conserto nenhum. Dizer "não foi possível ativar" nos dois seria
 * mandar alguém procurar uma solução que não existe — ou esconder a que existe.
 */

type Props = {
  facts: DeviceFacts
  /** Pede a permissão e registra a inscrição. Resolve quando terminou. */
  onSubscribe: () => Promise<void>
}

export function PushSection({ facts, onSubscribe }: Props) {
  const [ligando, setLigando] = useState(false)
  const [falha, setFalha] = useState<string | null>(null)
  const estado = pushState(facts)

  return (
    <section aria-label="Push" className="mt-4 border-t border-[var(--border)] pt-3">
      <h3 className="mb-2 text-xs text-[var(--text-muted)]">Avisos no celular</h3>

      {estado === 'unsupported' && (
        <p className="text-sm text-[var(--text-muted)]">
          Este navegador não faz push. As notificações continuam aparecendo aqui
          nesta tela.
        </p>
      )}

      {estado === 'needsInstall' && (
        <div className="text-sm text-[var(--text-muted)]">
          {/* A explicação inteira, e não um "instale o app". O porquê é o que
              faz alguém atravessar três toques num menu escondido. */}
          <p>
            No iPhone, o push só funciona com o app instalado na tela de início —
            é uma regra da Apple, não uma escolha nossa. Sem instalar, não existe
            aviso nenhum fora do app.
          </p>
          <p className="mt-2">
            Para instalar: toque em <strong>Compartilhar</strong> na barra do
            Safari, escolha <strong>Adicionar à Tela de Início</strong> e abra o
            Porquinho por lá. Depois volte aqui.
          </p>
        </div>
      )}

      {estado === 'denied' && (
        <p className="text-sm text-[var(--text-muted)]">
          {/* Não oferece um botão de "tentar de novo": o navegador não reabre o
              diálogo, e um botão que não faz nada é pior que nenhum. */}
          Você negou a permissão de notificação para este app. Só dá para mudar
          isso nos ajustes do navegador — as notificações continuam aqui nesta
          tela enquanto isso.
        </p>
      )}

      {(estado === 'canAsk' || estado === 'notSubscribed') && (
        <>
          <p className="text-sm text-[var(--text-muted)]">
            {estado === 'canAsk'
              ? 'Ative para receber no celular o que você marcou como "Push e no app" acima.'
              : 'A permissão já está dada; falta inscrever este aparelho.'}
          </p>
          <button
            type="button"
            disabled={ligando}
            className="mt-2 text-sm underline"
            onClick={() => {
              setLigando(true)
              setFalha(null)
              onSubscribe()
                .catch((error: unknown) => {
                  // **A mensagem de verdade vai para a tela.** "Não foi possível
                  // ativar agora" escondia o motivo, e "permissão negada",
                  // "service worker travado" e "chave inválida" pedem coisas
                  // diferentes de quem está olhando.
                  setFalha(error instanceof Error ? error.message : String(error))
                })
                .finally(() => {
                  setLigando(false)
                })
            }}
          >
            {ligando ? 'Ativando…' : 'Ativar avisos neste aparelho'}
          </button>
        </>
      )}

      {estado === 'subscribed' && (
        <p className="text-sm text-[var(--text-muted)]">
          Este aparelho está recebendo. Cada celular precisa ser ativado uma vez.
        </p>
      )}

      {falha !== null && (
        <p role="alert" className="mt-2 text-sm text-[var(--text-muted)]">
          {`Não foi possível ativar: ${falha}`}
        </p>
      )}
    </section>
  )
}
